import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import apiClient from '../api/apiClient';

const GoogleCallbackPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [status, setStatus] = useState('processing'); // 'processing' | 'success' | 'error'
    const [errorMessage, setErrorMessage] = useState('');
    const hasProcessedRef = useRef(false);

    useEffect(() => {
        // Prevent multiple executions - OAuth codes are single-use
        if (hasProcessedRef.current) {
            return;
        }

        const handleCallback = async () => {
            const params = new URLSearchParams(location.search);
            const code = params.get('code');
            const state = params.get('state');
            const error = params.get('error');

            if (error) {
                setStatus('error');
                setErrorMessage(error);
                hasProcessedRef.current = true;
                return;
            }

            if (!code || !state) {
                setStatus('error');
                setErrorMessage('Missing authorization code or state.');
                hasProcessedRef.current = true;
                return;
            }

            hasProcessedRef.current = true;

            try {
                // Forward the code and state to our backend
                // The backend will exchange code for tokens
                await apiClient.post('/google/callback', {
                    code,
                    state,
                });

                setStatus('success');
                // Redirect to sources page after a short delay
                setTimeout(() => {
                    navigate('/sources?bigquery-connected=true');
                }, 1500);
            } catch (err) {
                console.error('Google OAuth callback failed:', err);
                setStatus('error');
                setErrorMessage(err.response?.data?.detail || 'Failed to complete Google authentication.');
            }
        };

        handleCallback();
    }, [location, navigate]);

    return (
        <div className="min-h-[60vh] flex flex-col items-center justify-center p-8">
            <div className="w-full max-w-md bg-white dark:bg-gray-900 rounded-[40px] border border-gray-100 dark:border-gray-800 p-12 shadow-2xl flex flex-col items-center text-center">
                {status === 'processing' && (
                    <>
                        <div className="p-6 bg-blue-50 dark:bg-blue-900/20 text-blue-600 rounded-3xl mb-8">
                            <Loader2 className="w-12 h-12 animate-spin" />
                        </div>
                        <h2 className="text-2xl font-black text-gray-900 dark:text-white uppercase tracking-tighter mb-4">
                            Authenticating...
                        </h2>
                        <p className="text-sm text-gray-500 font-medium">
                            Please wait while we finalize your Google connection.
                        </p>
                    </>
                )}

                {status === 'success' && (
                    <>
                        <div className="p-6 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 rounded-3xl mb-8">
                            <CheckCircle2 className="w-12 h-12" />
                        </div>
                        <h2 className="text-2xl font-black text-gray-900 dark:text-white uppercase tracking-tighter mb-4">
                            Success!
                        </h2>
                        <p className="text-sm text-gray-500 font-medium">
                            Google account connected successfully. Redirecting you...
                        </p>
                    </>
                )}

                {status === 'error' && (
                    <>
                        <div className="p-6 bg-red-50 dark:bg-red-900/20 text-red-600 rounded-3xl mb-8">
                            <AlertCircle className="w-12 h-12" />
                        </div>
                        <h2 className="text-2xl font-black text-gray-900 dark:text-white uppercase tracking-tighter mb-4">
                            Connection Failed
                        </h2>
                        <p className="text-sm text-gray-500 font-medium mb-8">
                            {errorMessage}
                        </p>
                        <button
                            onClick={() => navigate('/sources')}
                            className="bg-gray-900 dark:bg-white text-white dark:text-gray-900 px-8 py-4 rounded-2xl font-black uppercase tracking-widest text-[11px] hover:scale-105 transition-transform"
                        >
                            Back to Sources
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};

export default GoogleCallbackPage;
